import type { ReactNode } from 'react'
import { registerWidget, type CellContentProps } from '../registry'
import { TypeGlyph } from '../TypeGlyph'
import { ChipLabel } from '../ChipLabel'
import { GroupedChips } from '../GroupedChips'
import { groupValues, serverGroups, type GroupSample } from '../valueGroups'
import { TypeIcon } from '../../sidebar/TypeIcon'
import { EntityReferenceEditor } from './reference-editor'

/** Legacy / actor value shape: `{type, id, name, icon, color}`. */
interface Ref {
  type?: string
  id?: string
  name?: string
  icon?: string
  color?: string
}

/** Wire shape for entity-reference values — the `*_record_id` + `*_kenning`
 *  pairing the monad API uses everywhere a record_id appears, plus the target's
 *  identity mark (resolved by the one chip seam, `resolve_chips_batch`). */
interface EntityRefWire {
  target_record_id?: string
  target_kenning?: string
  target_icon?: string
  target_color?: string
}

/** SEN-707 wire shape for reverse 1:n reference values — query-time resolved,
 *  capped targets + total count (see DisplayStore.get_reverse_values_batch). */
interface ReverseRefWire {
  reverse?: boolean
  count?: number
  targets?: EntityRefWire[]
}

/** A value's reference chips, each with its weight (how many values it
 *  stands for — a server group's exact count, else 1) and the server's
 *  sample records for that group. */
type WeightedChip = { chip: EntityRefWire; weight: number; samples?: GroupSample[] }

function isReverseWire(value: unknown): value is ReverseRefWire {
  return typeof value === 'object' && value !== null && (value as ReverseRefWire).reverse === true
}

/** Every entity-reference value shape → its chips. */
function toChips(value: unknown): WeightedChip[] {
  if (value == null || value === '') return []
  const fromServer = serverGroups(value)
  if (fromServer) {
    // records/values' grouped path answer: exact counts, computed server-side.
    return fromServer.flatMap((g) =>
      toChips(g.value).map((c) => ({ ...c, weight: c.weight * g.count, samples: g.samples })))
  }
  if (Array.isArray(value)) return value.flatMap(toChips)
  if (isReverseWire(value)) return (value.targets ?? []).map((chip) => ({ chip, weight: 1 }))
  if (typeof value === 'string') return [{ chip: { target_record_id: value }, weight: 1 }]
  if (typeof value === 'object') {
    const wire = value as EntityRefWire
    if (wire.target_record_id !== undefined) return [{ chip: wire, weight: 1 }]
    // Legacy {id, name, type, icon, color} shape.
    const ref = value as Ref
    const name = ref.name ?? (ref.type ? ref.type.charAt(0).toUpperCase() + ref.type.slice(1) : '')
    return [{ chip: { target_record_id: ref.id, target_kenning: name, target_icon: ref.icon, target_color: ref.color }, weight: 1 }]
  }
  return []
}

const CHIP_CLASS = 'inline-flex max-w-[12rem] items-center truncate rounded-full border border-border bg-muted px-1.5 py-0.5 text-xs text-foreground'

/**
 * THE entity-reference drawing (SEN-204): one pill per referenced record — the
 * target's declared icon and colour (a neutral glyph where the type declares
 * no icon) and its kenning (never the id; "unresolved" when it has none) — for
 * a single reference, a list, a reverse set and a path set alike.
 *
 * SEN-205: references to the SAME record (same id — never merely the same
 * kenning) are one pill with its exact count ("Retail ×500"); every pill is
 * always in the cell, compacting to its mark + count when labels do not fit.
 */
function ReferenceChips({
  value, targetTypeId, onNavigate, wrap = false,
}: {
  value: unknown
  targetTypeId?: string
  onNavigate?: (typeId: string, recordId: string) => void
  /** A roomy surface (a record page's property row): the pills wrap and keep their full kenning, never compact
   *  to a bare mark. The measured compaction is for a table cell's one line; in a 288px rail it hid the name
   *  of every long-named reference (X14, SEN-439). */
  wrap?: boolean
}) {
  const weighted = toChips(value)
  const groups = groupValues(
    weighted,
    // A chip without an id (legacy shapes) is its own group: never merged by label.
    (w, index) => w.chip.target_record_id ?? `#${index}`,
    (w) => w.chip.target_kenning || 'unresolved',
    (w) => w.weight,
  ).map((g) => ({ ...g, samples: g.sample.samples }))
  const isNavigable = Boolean(onNavigate && targetTypeId)
  const pill = wrap ? CHIP_CLASS.replace('max-w-[12rem] ', 'max-w-full ') : CHIP_CLASS
  const draw = (group: { sample: WeightedChip }, badge: ReactNode, compact: boolean) => {
    const chip = group.sample.chip
    const label = (
      <span data-testid="set-group-label" className={compact ? 'sr-only' : 'inline-flex min-w-0 items-center truncate'}>
        {compact ? (chip.target_kenning || 'unresolved') : (
          <ChipLabel
            kenning={chip.target_kenning ?? ''}
            icon={chip.target_icon}
            color={chip.target_color}
            placeholder
            emptyLabel="unresolved"
          />
        )}
      </span>
    )
    const mark = compact ? (
      <ChipLabel kenning={chip.target_kenning ?? ''} icon={chip.target_icon} color={chip.target_color} placeholder iconOnly />
    ) : null
    const body = <>{mark}{label}{badge}</>
    if (isNavigable && chip.target_record_id) {
      return (
        <button
          type="button"
          data-testid="ref-chip-nav"
          className={`${pill} cursor-pointer hover:underline`}
          onClick={() => onNavigate!(targetTypeId!, chip.target_record_id!)}
        >
          <span data-testid="ref-chip" className="inline-flex min-w-0 items-center truncate">{body}</span>
        </button>
      )
    }
    return <span data-testid="ref-chip" className={pill}>{body}</span>
  }
  if (wrap) {
    // every pill in full, wrapping onto further lines (no measured compaction)
    return (
      <span data-testid="ref-chips" className="flex w-full min-w-0 flex-wrap items-center gap-1">
        {groups.length === 0 ? <span className="text-muted-foreground">—</span> : groups.map((group) => (
          <span key={group.key} data-testid="set-group" className="inline-flex min-w-0 max-w-full items-center">
            {draw(group, group.count > 1
              ? <span data-testid="group-count" className="shrink-0 pl-1 text-[11px] font-medium text-muted-foreground">×{group.count}</span>
              : null, false)}
          </span>
        ))}
      </span>
    )
  }
  return (
    <span data-testid="ref-chips" className="flex w-full min-w-0 items-center">
      <GroupedChips groups={groups} renderGroup={draw} />
    </span>
  )
}

function EntityReference({ value, config, onNavigate }: CellContentProps) {
  const targetTypeId = (config as { target_type_id?: string } | undefined)?.target_type_id
  return <ReferenceChips value={value} targetTypeId={targetTypeId} onNavigate={onNavigate} />
}

/** The record page's property row: the same pills, wrapped and never compacted (X14). */
function EntityReferenceDetail({ value, config, onNavigate }: CellContentProps) {
  const targetTypeId = (config as { target_type_id?: string } | undefined)?.target_type_id
  return <ReferenceChips value={value} targetTypeId={targetTypeId} onNavigate={onNavigate} wrap />
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

/** An ACTOR (a person) — Attio's avatar: a circle with name initials. */
function ActorReference({ value }: CellContentProps) {
  const actor = toChips(value)[0]?.chip
  if (!actor) return <span className="text-muted-foreground">—</span>
  const name = actor.target_kenning ?? ''
  return (
    <span className="inline-flex max-w-full items-center gap-2 truncate">
      {actor.target_icon
        ? <TypeIcon icon={actor.target_icon} name={name || '?'} color={actor.target_color} />
        : <TypeGlyph glyph={name ? initials(name) : '?'} shape="circle" />}
      <span className="truncate text-foreground">{name || 'reference'}</span>
      {!name && <span className="shrink-0 text-xs italic text-muted-foreground">unresolved</span>}
    </span>
  )
}

registerWidget('actor-reference', { read: ActorReference, defaultWidth: 200 })
registerWidget('entity-reference', {
  read: EntityReference, detail: EntityReferenceDetail, write: EntityReferenceEditor, defaultWidth: 200, readsSets: true,
})
